import { useHasBeenInViewport } from "common/hooks/interaction/useHasBeenInViewport"
import { isYouTubeUrl, normalizeUrl, thumbnailUrl } from "common/lib/youtube"
import { useTranslations } from "modules/i18n/hooks/useTranslations"

export interface VideoProps {
  /** Video source URL. */
  src: string
  /** Caption for the video. */
  caption?: string
  /** Disable user interaction for the video. */
  disable?: boolean
}

export const Video = ({ src, caption, disable }: VideoProps) => {
  const t = useTranslations("blocks.video")
  const { setRef, hasBeenInViewport } = useHasBeenInViewport(100)
  const youtubeVideo = disable ? (
    <img src={thumbnailUrl(src, "hq")} alt={t("youtube_thumbnail_label")} className="w-full" />
  ) : (
    <iframe
      className="aspect-video w-full"
      src={normalizeUrl(src)}
      frameBorder="0"
      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
      allowFullScreen
      title={t("youtube_label")}
    ></iframe>
  )

  return isYouTubeUrl(src) ? (
    <figure
      data-testid="youtube-figure"
      className="my-4 aspect-video max-w-(--breakpoint-lg) lg:my-9"
      ref={setRef}
    >
      <div className="bg-black">{hasBeenInViewport && youtubeVideo}</div>
      {caption && <figcaption className="mt-1 text-sm text-gray-700">{caption}</figcaption>}
    </figure>
  ) : (
    <figure className="my-4 max-w-(--breakpoint-lg) lg:my-9">
      {/* We should have proper tracks for accessibility,
       * but, seems unlikely to have them anytime soon. So, don't spam warnings.
       */}
      {/* eslint-disable-next-line jsx-a11y/media-has-caption */}
      <video
        src={src}
        data-testid="video-player"
        controls
        className="w-full"
        aria-label={t("generic_label")}
      />
      {caption && <figcaption className="mt-1 text-sm text-gray-700">{caption}</figcaption>}
    </figure>
  )
}
